import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SecureProfilePhoto } from '@/components/profile/SecureProfilePhoto';
import { ArrowLeft, Users, Save, UserCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PlayerData {
  user_id: string;
  position: string | null;
  team_id: number;
  team_name: string;
  username: string;
  profile_photo_url: string | null;
}

const POSITIONS = [
  'Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Striker',
  'Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center',
  'Pitcher', 'Catcher', 'Infielder', 'Outfielder',
  'Quarterback', 'Running Back', 'Wide Receiver', 'Linebacker', 'Lineman',
  'Wing', 'Prop', 'Hooker', 'Lock', 'Flanker', 'Number 8', 'Scrum-half', 'Fly-half',
  'Setter', 'Libero', 'Outside Hitter', 'Middle Blocker', 'Opposite Hitter',
  'Goalie', 'Defense', 'Left Wing', 'Right Wing',
  'Utility', 'Captain', 'Substitute', 'Other'
];

const ManagePlayers = () => {
  const navigate = useNavigate();
  const { user } = useInternalAuth();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [players, setPlayers] = useState<PlayerData[]>([]);
  const [teams, setTeams] = useState<{ team_id: number; team_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editedPositions, setEditedPositions] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user?.id) loadData();
  }, [user?.id]);

  const loadData = async () => {
    try {
      // Get connected teams from profile
      const { data: profileData } = await supabase
        .from('internal_user_profiles')
        .select('connected_teams')
        .eq('user_id', user!.id)
        .single();

      const connectedTeamIds = (profileData?.connected_teams as number[]) || [];

      // Also get teams created by this user
      const { data: createdTeams } = await supabase
        .from('teams')
        .select('team_id')
        .eq('created_by', user!.id);

      const createdIds = createdTeams?.map(t => t.team_id) || [];
      const allTeamIds = [...new Set([...connectedTeamIds, ...createdIds])];

      if (allTeamIds.length === 0) {
        setLoading(false);
        return;
      }

      // Get team names
      const { data: teamsData } = await supabase
        .from('teams')
        .select('team_id, team_name')
        .in('team_id', allTeamIds);

      setTeams(teamsData || []);

      // Get all players across these teams
      const { data: teamPlayers } = await supabase
        .from('team_players')
        .select('user_id, position, team_id')
        .in('team_id', allTeamIds);

      if (!teamPlayers || teamPlayers.length === 0) {
        setPlayers([]);
        setLoading(false);
        return;
      }

      // Get player profiles from internal_users
      const playerIds = [...new Set(teamPlayers.map(p => p.user_id))];
      const { data: profiles } = await supabase
        .from('internal_users')
        .select('id, username')
        .in('id', playerIds);

      // Get profile photos from internal_user_profiles
      const { data: profilePhotos } = await supabase
        .from('internal_user_profiles')
        .select('user_id, profile_photo_url')
        .in('user_id', playerIds);

      const internalMap = new Map(profiles?.map(p => [p.id, p]) || []);
      const photoMap = new Map(profilePhotos?.map(p => [p.user_id, p.profile_photo_url]) || []);
      const teamMap = new Map(teamsData?.map(t => [t.team_id, t.team_name]) || []);

      const playerList: PlayerData[] = teamPlayers.map(tp => {
        const internal = internalMap.get(tp.user_id);
        const photo = photoMap.get(tp.user_id) || null;
        return {
          user_id: tp.user_id,
          position: tp.position,
          team_id: tp.team_id,
          team_name: teamMap.get(tp.team_id) || 'Unknown Team',
          username: internal?.username || `Player-${tp.user_id.slice(0, 6)}`,
          profile_photo_url: photo,
        };
      });

      setPlayers(playerList);
    } catch (err) {
      console.error('Error loading players:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePositionChange = (userId: string, teamId: number, position: string) => {
    const key = `${userId}-${teamId}`;
    setEditedPositions(prev => ({ ...prev, [key]: position }));
  };

  const handleSavePosition = async (userId: string, teamId: number) => {
    const key = `${userId}-${teamId}`;
    const newPosition = editedPositions[key];
    if (!newPosition) return;

    setSaving(key);
    try {
      const { error } = await supabase
        .from('team_players')
        .update({ position: newPosition })
        .eq('user_id', userId)
        .eq('team_id', teamId);

      if (error) throw error;

      setPlayers(prev => prev.map(p =>
        p.user_id === userId && p.team_id === teamId
          ? { ...p, position: newPosition }
          : p
      ));
      setEditedPositions(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast({ title: 'Position updated', description: 'Player position saved successfully.' });
    } catch (err) {
      console.error('Error saving position:', err);
      toast({ title: 'Error', description: 'Failed to update position.', variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  const handleMoveTeam = async (userId: string, fromTeamId: number, toTeamId: number) => {
    try {
      // Remove from old team
      const { error: deleteErr } = await supabase
        .from('team_players')
        .delete()
        .eq('user_id', userId)
        .eq('team_id', fromTeamId);

      if (deleteErr) throw deleteErr;

      // Add to new team
      const { error: insertErr } = await supabase
        .from('team_players')
        .insert({ user_id: userId, team_id: toTeamId });

      if (insertErr) throw insertErr;

      toast({ title: 'Player moved', description: 'Player has been moved to the new team.' });
      loadData();
    } catch (err) {
      console.error('Error moving player:', err);
      toast({ title: 'Error', description: 'Failed to move player.', variant: 'destructive' });
    }
  };

  // Group players by team
  const playersByTeam = teams.map(team => ({
    ...team,
    players: players.filter(p => p.team_id === team.team_id),
  }));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}

      <main className={`flex-1 container mx-auto px-4 ${isMobile ? 'pt-20 pb-24' : 'pt-24 py-8'}`}>
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <Users className="h-8 w-8 text-sports-green" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">Manage Players</h1>
            <p className="text-muted-foreground">View and update your roster across all teams</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-12">Loading players...</div>
        ) : playersByTeam.every(t => t.players.length === 0) ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <UserCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg mb-2">No players on your teams yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Players will appear here once they join your teams
              </p>
              <Button onClick={() => navigate('/team-management')} variant="outline">
                Go to Team Management
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {playersByTeam.filter(t => t.players.length > 0).map(team => (
              <Card key={team.team_id} className="bg-card border-border">
                <CardHeader className="border-b border-border pb-4">
                  <CardTitle className="text-xl text-foreground flex items-center gap-3">
                    <div className="h-8 w-1.5 bg-sports-green rounded-full" />
                    <span className="text-2xl font-bold text-foreground">{team.team_name}</span>
                    <span className="text-sm text-muted-foreground font-normal ml-1">
                      ({team.players.length} player{team.players.length !== 1 ? 's' : ''})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {team.players.map(player => {
                      const key = `${player.user_id}-${player.team_id}`;
                      const hasEdit = key in editedPositions;
                      return (
                        <Card key={key} className="bg-muted/50 border-border">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <SecureProfilePhoto
                                filePath={player.profile_photo_url}
                                fallbackInitials={player.username.charAt(0).toUpperCase()}
                                size="sm"
                                className="ring-2 ring-sports-green/20"
                              />
                              <div>
                                <p
                                  className="font-semibold text-foreground cursor-pointer hover:text-sports-green transition-colors"
                                  onClick={() => navigate(`/${player.username}`)}
                                >
                                  @{player.username}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {player.position || 'No position set'}
                                </p>
                              </div>
                            </div>

                            {/* Position selector */}
                            <div className="space-y-2">
                              <label className="text-xs text-muted-foreground">Position</label>
                              <div className="flex gap-2">
                                <Select
                                  value={editedPositions[key] || player.position || ''}
                                  onValueChange={(val) => handlePositionChange(player.user_id, player.team_id, val)}
                                >
                                  <SelectTrigger className="flex-1 bg-background border-border">
                                    <SelectValue placeholder="Select position" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-background border-border max-h-60">
                                    {POSITIONS.map(pos => (
                                      <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {hasEdit && (
                                  <Button
                                    size="icon"
                                    onClick={() => handleSavePosition(player.user_id, player.team_id)}
                                    disabled={saving === key}
                                    className="bg-sports-green hover:bg-sports-green/80 text-black"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>

                              {/* Move to another team */}
                              {teams.length > 1 && (
                                <div>
                                  <label className="text-xs text-muted-foreground">Move to team</label>
                                  <Select
                                    onValueChange={(val) => handleMoveTeam(player.user_id, player.team_id, Number(val))}
                                  >
                                    <SelectTrigger className="bg-background border-border">
                                      <SelectValue placeholder="Move to..." />
                                    </SelectTrigger>
                                    <SelectContent className="bg-background border-border">
                                      {teams
                                        .filter(t => t.team_id !== player.team_id)
                                        .map(t => (
                                          <SelectItem key={t.team_id} value={String(t.team_id)}>
                                            {t.team_name}
                                          </SelectItem>
                                        ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default ManagePlayers;
